import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  FolderOpen,
  Settings,
  Users,
  MessageSquare,
  TrendingUp,
  Eye,
  Calendar,
  MoreHorizontal,
  Pencil,
  ArrowRight,
  Sparkles,
  LayoutDashboard
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProjects } from '@/hooks/use-projects';
import { useServices } from '@/hooks/use-services';
import { useClients } from '@/hooks/use-clients';
import { useTestimonials } from '@/hooks/use-testimonials';
import { Skeleton } from '@/components/ui/skeleton';

export default function AdminHome() {
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: services = [], isLoading: servicesLoading } = useServices();
  const { data: clients = [], isLoading: clientsLoading } = useClients();
  const { data: testimonials = [], isLoading: testimonialsLoading } = useTestimonials();

  const isLoading = projectsLoading || servicesLoading || clientsLoading || testimonialsLoading;

  // Calculate statistics
  const totalProjects = projects.length;
  const featuredProjects = projects.filter(p => p.featured).length;

  // Recent projects (last 5 sorted by year/id usually, here relying on default order or year)
  const recentProjects = [...projects]
    .sort((a, b) => b.year - a.year)
    .slice(0, 5);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  if (isLoading) {
    return (
      <div className="space-y-8 p-6">
        <Skeleton className="h-[200px] w-full rounded-3xl" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 p-8 text-white shadow-2xl"
      >
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-sm font-medium backdrop-blur-md border border-white/20 mb-2">
              <Sparkles className="mr-2 h-4 w-4 text-yellow-300" />
              Painel Administrativo
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Bem-vindo de volta, Cássio!</h1>
            <p className="text-blue-100 max-w-lg">
              Aqui está o resumo da performance do seu portfólio. Você tem {projects.length} projetos publicados e {clients.length} clientes ativos.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-blue-50 border-0 shadow-lg font-semibold">
              <Link to="/admin/projetos/novo">
                <Plus className="mr-2 h-5 w-5" /> Novo Projeto
              </Link>
            </Button>
          </div>
        </div>

        {/* Decorative circles */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />
      </motion.div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid gap-6 md:grid-cols-2 lg:grid-cols-4"
      >
        <motion.div variants={item}>
          <Card className="hover:-translate-y-1 transition-all duration-300 hover:shadow-xl border-t-4 border-t-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between space-y-0 pb-2">
                <p className="text-sm font-medium text-muted-foreground">Projetos Totais</p>
                <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <FolderOpen className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="mt-2">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalProjects}</div>
                <p className="text-xs text-muted-foreground mt-1 flex items-center">
                  <Badge variant="outline" className="text-blue-500 bg-blue-500/5 border-blue-200 mr-2">{featuredProjects} Destaques</Badge>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="hover:-translate-y-1 transition-all duration-300 hover:shadow-xl border-t-4 border-t-purple-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between space-y-0 pb-2">
                <p className="text-sm font-medium text-muted-foreground">Serviços Ativos</p>
                <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Settings className="h-5 w-5 text-purple-600" />
                </div>
              </div>
              <div className="mt-2">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{services.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Categorias oferecidas
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="hover:-translate-y-1 transition-all duration-300 hover:shadow-xl border-t-4 border-t-emerald-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between space-y-0 pb-2">
                <p className="text-sm font-medium text-muted-foreground">Clientes</p>
                <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
              <div className="mt-2">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{clients.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Parceiros de negócios
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="hover:-translate-y-1 transition-all duration-300 hover:shadow-xl border-t-4 border-t-amber-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between space-y-0 pb-2">
                <p className="text-sm font-medium text-muted-foreground">Depoimentos</p>
                <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              <div className="mt-2">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{testimonials.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Feedbacks recebidos
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
        {/* Recent Projects Table */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="col-span-4"
        >
          <Card className="h-full overflow-hidden border-0 shadow-lg">
            <CardHeader className="bg-muted/30 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Projetos Recentes</CardTitle>
                  <CardDescription className="mt-1">
                    Últimas atualizações do seu portfólio
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" asChild className="hover:bg-background">
                  <Link to="/admin/projetos" className="flex items-center gap-1 text-primary">
                    Ver todos <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-6">Projeto</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Ano</TableHead>
                    <TableHead className="text-right pr-6">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentProjects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center h-32 text-muted-foreground">
                        <div className="flex flex-col items-center justify-center">
                          <FolderOpen className="h-8 w-8 text-muted-foreground/30 mb-2" />
                          <p>Nenhum projeto encontrado.</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentProjects.map((project) => (
                      <TableRow key={project.id} className="group hover:bg-muted/30 transition-colors">
                        <TableCell className="pl-6 font-medium">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden ring-1 ring-border/50 group-hover:ring-primary/30 transition-all">
                              {project.coverImage ? (
                                <img
                                  src={project.coverImage}
                                  alt={project.title}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <FolderOpen className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold">{project.title}</span>
                              {project.featured && (
                                <Badge variant="secondary" className="w-fit text-[10px] h-4 px-1 bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20 border-yellow-200">Destaque</Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{project.client || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-normal">{project.year}</Badge>
                        </TableCell>
                        <TableCell className="text-right pr-6">
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-blue-100 hover:text-blue-600" asChild>
                              <Link to={`/admin/projetos/${project.id}/editar`}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-purple-100 hover:text-purple-600" asChild>
                              <Link to={`/projetos/${project.id}`} target="_blank">
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="col-span-3 space-y-6"
        >
          <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutDashboard className="h-5 w-5 text-primary" />
                Ações Rápidas
              </CardTitle>
              <CardDescription>Atalhos para tarefas frequentes</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <Button asChild variant="outline" className="justify-start h-auto py-6 px-6 relative overflow-hidden group border-muted hover:border-blue-500/50 hover:bg-blue-50/50 transition-all dark:hover:bg-blue-900/10">
                <Link to="/admin/servicos/novo">
                  <div className="bg-blue-500/10 p-3 rounded-xl mr-4 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                    <Settings className="h-6 w-6 text-blue-600 group-hover:text-white" />
                  </div>
                  <div className="flex flex-col items-start z-10">
                    <span className="font-semibold text-base group-hover:text-blue-700 dark:group-hover:text-blue-300">Novo Serviço</span>
                    <span className="text-xs text-muted-foreground group-hover:text-blue-600/70 dark:group-hover:text-blue-300/70">Adicionar categoria</span>
                  </div>
                  <ArrowRight className="absolute right-6 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-blue-500" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="justify-start h-auto py-6 px-6 relative overflow-hidden group border-muted hover:border-emerald-500/50 hover:bg-emerald-50/50 transition-all dark:hover:bg-emerald-900/10">
                <Link to="/admin/clientes/novo">
                  <div className="bg-emerald-500/10 p-3 rounded-xl mr-4 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                    <Users className="h-6 w-6 text-emerald-600 group-hover:text-white" />
                  </div>
                  <div className="flex flex-col items-start z-10">
                    <span className="font-semibold text-base group-hover:text-emerald-700 dark:group-hover:text-emerald-300">Novo Cliente</span>
                    <span className="text-xs text-muted-foreground group-hover:text-emerald-600/70 dark:group-hover:text-emerald-300/70">Cadastrar parceiro</span>
                  </div>
                  <ArrowRight className="absolute right-6 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-emerald-500" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="justify-start h-auto py-6 px-6 relative overflow-hidden group border-muted hover:border-amber-500/50 hover:bg-amber-50/50 transition-all dark:hover:bg-amber-900/10">
                <Link to="/admin/depoimentos/novo">
                  <div className="bg-amber-500/10 p-3 rounded-xl mr-4 group-hover:bg-amber-500 group-hover:text-white transition-colors">
                    <MessageSquare className="h-6 w-6 text-amber-600 group-hover:text-white" />
                  </div>
                  <div className="flex flex-col items-start z-10">
                    <span className="font-semibold text-base group-hover:text-amber-700 dark:group-hover:text-amber-300">Novo Depoimento</span>
                    <span className="text-xs text-muted-foreground group-hover:text-amber-600/70 dark:group-hover:text-amber-300/70">Adicionar feedback</span>
                  </div>
                  <ArrowRight className="absolute right-6 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-amber-500" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
